import { useState } from 'react';
import type { Plan, Engagement } from './PlanSelector';

interface OrderSummaryProps {
  plan: Plan;
  engagement: Engagement;
  onCheckout: (email: string) => void;
  loading: boolean;
}

const ENGAGEMENT_LABELS: Record<Engagement, string> = {
  sans: 'Sans engagement',
  '6mois': 'Engagement 6 mois',
  '12mois': 'Engagement 12 mois',
};

const TYPE_LABELS: Record<string, string> = {
  vitrine: 'Site Vitrine',
  ecommerce: 'Site E-commerce',
};

export default function OrderSummary({ plan, engagement, onCheckout, loading }: OrderSummaryProps) {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const currentPrice = plan.prices[engagement];
  const isEmerald = plan.color === 'emerald';
  const isAmber = plan.color === 'amber';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Veuillez entrer un e-mail valide');
      return;
    }
    setEmailError('');
    onCheckout(email);
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-slate-100 overflow-hidden sticky top-24">
      {/* Header */}
      <div
        className={`p-5 text-white ${
          isEmerald
            ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
            : isAmber
            ? 'bg-gradient-to-r from-amber-500 to-yellow-500'
            : 'bg-gradient-to-r from-teal-500 to-emerald-400'
        }`}
      >
        <p className="text-xs font-bold opacity-80 uppercase tracking-wide mb-1">Récapitulatif</p>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
            <i className={`${plan.icon} text-lg`}></i>
          </div>
          <div>
            <p className="text-base font-black leading-tight">
              {TYPE_LABELS[plan.type]} {plan.name}
            </p>
            <p className="text-xs opacity-80">{ENGAGEMENT_LABELS[engagement]}</p>
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* Prix */}
        <div className="flex items-end justify-between mb-5 pb-5 border-b border-slate-100">
          <div>
            <p className="text-xs text-slate-500 mb-0.5">Abonnement mensuel</p>
            <div className="flex items-baseline gap-1">
              <span
                className={`text-3xl font-black ${
                  isEmerald ? 'text-emerald-600' : isAmber ? 'text-amber-600' : 'text-teal-600'
                }`}
              >
                {currentPrice}€
              </span>
              <span className="text-sm text-slate-400">/mois</span>
            </div>
            {engagement !== 'sans' && (
              <p className="text-xs text-slate-400 line-through mt-0.5">
                {plan.prices.sans}€/mois sans engagement
              </p>
            )}
          </div>
          {engagement !== 'sans' && (
            <span className="bg-amber-50 border border-amber-200 text-amber-700 text-xs font-black px-2.5 py-1.5 rounded-full">
              {engagement === '6mois' ? '-23%' : '-35%'}
            </span>
          )}
        </div>

        {/* Features recap */}
        <div className="mb-5">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2.5">Inclus dans votre plan</p>
          <div className="grid grid-cols-1 gap-1.5">
            {plan.features.slice(0, 5).map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`ri-check-line text-sm ${
                      isEmerald ? 'text-emerald-500' : isAmber ? 'text-amber-500' : 'text-teal-500'
                    }`}
                  ></i>
                </div>
                <span className="text-xs text-slate-700">{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Reassurance */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          {[
            { icon: 'ri-shield-check-line', label: 'Paiement sécurisé' },
            { icon: 'ri-refresh-line', label: 'Annulable à tout moment' },
            { icon: 'ri-timer-flash-line', label: 'Livraison en 7j max' },
            { icon: 'ri-customer-service-2-line', label: 'Support inclus' },
          ].map((r, i) => (
            <div key={i} className="flex items-center gap-1.5 bg-slate-50 rounded-lg px-2.5 py-2">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className={`${r.icon} text-xs text-emerald-500`}></i>
              </div>
              <span className="text-xs text-slate-600 font-medium leading-tight">{r.label}</span>
            </div>
          ))}
        </div>

        {/* Email field */}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Votre adresse e-mail
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-line text-slate-400 text-sm"></i>
              </div>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votreemail@exemple.com"
                className="w-full pl-9 pr-3 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-400 focus:outline-none text-sm text-slate-900 bg-white transition-colors"
              />
            </div>
            {emailError && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>
                {emailError}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-black text-sm text-white transition-all duration-300 cursor-pointer whitespace-nowrap ${
              loading
                ? 'opacity-70 cursor-not-allowed'
                : 'hover:scale-[1.02] hover:shadow-lg active:scale-95'
            } ${
              isEmerald
                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:shadow-emerald-500/30'
                : isAmber
                ? 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:shadow-amber-500/30'
                : 'bg-gradient-to-r from-teal-500 to-emerald-400 hover:shadow-teal-500/30'
            }`}
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base"></i>
                <span>Redirection en cours…</span>
              </>
            ) : (
              <>
                <i className="ri-secure-payment-line text-base"></i>
                <span>Payer {currentPrice}€/mois</span>
                <i className="ri-arrow-right-line text-sm"></i>
              </>
            )}
          </button>
        </form>

        <div className="flex items-center justify-center gap-2 mt-3">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-stripe-line text-slate-400 text-sm"></i>
          </div>
          <p className="text-xs text-slate-400 text-center">
            Paiement sécurisé par <strong>Stripe</strong> · Aucune carte enregistrée avant confirmation
          </p>
        </div>
      </div>
    </div>
  );
}
